import React, { useState, useEffect, useRef } from 'react'
import { Container, Tabs, Tab, Box, GlobalStyles, Snackbar, Alert } from '@mui/material'
import PropTypes from 'prop-types'
import GeneralInfoForm from './generalInfo'
import Gallery from './gallery'
import Catalogue from 'components/organisms/catalogue/container'
import Tariffs from './tariffs'
import OpeningTime from './opening'
import Services from './services';
import Communities from './communities';
import VenueActivationChecklist from 'components/organisms/catalogue/components/VenueActivationChecklist';
import { getVenueStaff, updateManagementWorkArea, updateVenue } from 'api'

// CSS animation for highlighting missing fields
const highlightStyles = {
  '@keyframes highlightPulse': {
    '0%': {
      boxShadow: '0 0 0 0 rgba(255, 152, 0, 0.7)',
      borderColor: 'rgba(255, 152, 0, 1)',
    },
    '50%': {
      boxShadow: '0 0 0 10px rgba(255, 152, 0, 0)',
      borderColor: 'rgba(255, 152, 0, 0.7)',
    },
    '100%': {
      boxShadow: '0 0 0 0 rgba(255, 152, 0, 0)',
      borderColor: 'rgba(255, 152, 0, 1)',
    },
  },
  '.highlight-missing-field': {
    animation: 'highlightPulse 1s ease-in-out 3',
    border: '2px solid rgba(255, 152, 0, 1) !important',
    borderRadius: '8px',
    transition: 'all 0.3s ease',
  },
};

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box p={3}>
          {children}
        </Box>
      )}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.any.isRequired,
  value: PropTypes.any.isRequired,
};

function VenueSettings({ slug }) {
  const [tabValue, setTabValue] = useState(0);
  const [venueData, setVenueData] = useState({})
  const [, setLoading] = useState(false)
  const [, setError] = useState('')
  const [isActivating, setIsActivating] = useState(false)
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' })
  
  // Ref for tabs container to scroll to
  const tabsRef = useRef(null);

  const handleChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Handler to activate the venue
  const handleActivateVenue = async () => {
    setIsActivating(true);
    try {
      await updateVenue(slug, { status: 'AC' });
      await refreshVenueData();
      setSnackbar({
        open: true,
        message: '🎉 Venue activated successfully! Your venue is now live and bookable.',
        severity: 'success'
      });
    } catch (err) {
      console.error('Error activating venue:', err);
      const errorMessage = err?.response?.data?.error || err?.response?.data?.validation_errors?.join(', ') || 'Failed to activate venue';
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setIsActivating(false);
    }
  };

  // Function to refresh venue data - can be called by child components after updates
  const refreshVenueData = async () => {
    try {
      const response = await getVenueStaff(slug)
      setVenueData(response?.venue)
    } catch (err) {
      console.error('Error refreshing venue data:', err)
    }
  };

  useEffect(() => {
    const fetchVenueData = async () => {
      setLoading(true)
      try {
        const response = await getVenueStaff(slug)
        setVenueData(response?.venue)
      } catch {
        setError('An error occurred while fetching the venue data.')
      } finally {
        setLoading(false)
      }
    }

    fetchVenueData()
  }, [slug])

  // Handle click on checklist item - navigate to appropriate tab and scroll to specific field
  const handleChecklistRequirementClick = (tabId, fieldId) => {
    const tabMap = {
      'general': 0,
      'gallery': 1,
      'catalogue': 2,
      'opening': 3,
      'services': 4
    };
    const tabIndex = tabMap[tabId];
    if (tabIndex !== undefined) {
      setTabValue(tabIndex);
      
      // Scroll to specific field after tab switch and add highlight
      setTimeout(() => {
        const targetElement = fieldId ? document.getElementById(fieldId) : tabsRef.current;
        
        if (targetElement) {
          // Scroll to the element with offset for better visibility
          targetElement.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center'
          });
          
          // Add highlight animation
          targetElement.classList.add('highlight-missing-field');
          
          // Remove highlight after animation completes (3 pulses × 1s = 3s)
          setTimeout(() => {
            targetElement.classList.remove('highlight-missing-field');
          }, 3500);
        } else if (tabsRef.current) {
          // Fallback to scrolling to tabs if field not found
          tabsRef.current.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start'
          });
        }
      }, 150); // Slightly longer delay to ensure tab content has rendered
    }
  };

  return (
    <>
      {/* Inject highlight animation styles */}
      <GlobalStyles styles={highlightStyles} />
      
      <Container maxWidth="md">
        {/* Activation Checklist - Show at top across all tabs */}
        {venueData && (
          <Box mt={3}>
            <VenueActivationChecklist
              venueData={venueData}
              onRequirementClick={handleChecklistRequirementClick}
              onActivateVenue={handleActivateVenue}
              isActivating={isActivating}
            />
          </Box>
        )}

        {/* Tab Selector */}
        <Box ref={tabsRef}>
          <Tabs value={tabValue} onChange={handleChange} aria-label="Venue Tabs">
            <Tab label="General Info" />
            <Tab label="Gallery" />
            <Tab label="Catalogue" />
            <Tab label="Opening time" />
            <Tab label="Services" />
            <Tab label="Communities" />
            {/* <Tab label="Tariffs" /> */}

          </Tabs>
        </Box>

        {/* Tab Panels */}
        <TabPanel value={tabValue} index={0}>
        <GeneralInfoForm venueData={venueData} onVenueUpdate={refreshVenueData} />
      </TabPanel>
      <TabPanel value={tabValue} index={1}>
        <Gallery venueData={venueData} onVenueUpdate={refreshVenueData} />
      </TabPanel>
      <TabPanel value={tabValue} index={2}>
        <Catalogue venueData={venueData} onVenueUpdate={refreshVenueData} />
      </TabPanel>
      <TabPanel value={tabValue} index={3}>
        <OpeningTime venueData={venueData} onVenueUpdate={refreshVenueData} />
      </TabPanel>
      <TabPanel value={tabValue} index={4}>
        <Services venueData={venueData} onVenueUpdate={refreshVenueData} />
      </TabPanel>
      <TabPanel value={tabValue} index={5}>
        <Communities venueData={venueData} onVenueUpdate={refreshVenueData} />
      </TabPanel>
      <TabPanel value={tabValue} index={6}>
        <Tariffs slug={slug} />
      </TabPanel>
      </Container>
      
      {/* Snackbar for activation feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

VenueSettings.propTypes = {
  slug: PropTypes.string.isRequired,
};

export default VenueSettings;
